// ViewTeacherModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalCheckboxes } from '../../../components/GlobalComponents/GlobalCheckboxes';

/* ─── Types ──────────────────────────────────────────────────────────── */
export interface TeacherAssignment {
  id: string;  // ✅ Changed from number to string
  class_id: string;  // ✅ Changed from number to string
  class_name: string;
  sections: string[];
  subject_id: string;  // ✅ Changed from number to string
  subject_name: string;
  created_at: string;
  updated_at: string;
}

export interface TeacherSummary {
  total_assignments: number;
  total_subjects: number;
  total_classes: number;
  total_sections: number;
}

export interface TeacherRow {
  id: string;  // ✅ Changed from number to string
  name: string;
  email_id: string;
  contact_no: string;
  gender: string;
  assignments: TeacherAssignment[];
  summary: TeacherSummary;
}

// Selection state for bulk remove: { [assignmentId]: Set of section strings, or 'ALL' }
type SelectionMap = Record<string, Set<string> | 'ALL'>;  // ✅ Changed from number to string

interface ViewTeacherModalProps {
  open: boolean;
  onClose: () => void;
  teacher: TeacherRow | null;
  onChanged?: () => void;
}

/* ─── Helpers ─────────────────────────────────────────────────────── */
const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const AVATAR_COLORS = [
  { bg: '#dbeafe', color: '#1e40af' },
  { bg: '#dcfce7', color: '#166534' },
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#ede9fe', color: '#4c1d95' },
  { bg: '#ffedd5', color: '#9a3412' },
];

const avatarColor = (id: string | number) => {
  const numId = typeof id === 'string' ? parseInt(id) || 0 : id;
  return AVATAR_COLORS[numId % AVATAR_COLORS.length];
};

// ✅ Helper to ensure we always have a string ID
const getId = (item: any): string => {
  return String(item.id || item._id || item.ID || '');
};

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
const ViewTeacherModal: React.FC<ViewTeacherModalProps> = ({ open, onClose, teacher, onChanged }) => {
  const toast = useRef<Toast>(null);

  const [data, setData] = useState<TeacherRow | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [selection, setSelection] = useState<SelectionMap>({});
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /* ── Load / reset on open ── */
  useEffect(() => {
    if (!open) {
      setData(null);
      setSelection({});
      return;
    }
    if (!teacher) return;
    setData(teacher);
    setSelection({});
    setIsClosing(false);
    refreshAssignments(teacher.id);
  }, [open, teacher?.id]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const refreshAssignments = async (teacherId: string) => {  // ✅ Changed to string
    setRefreshing(true);
    try {
      // ✅ Use the correct API endpoint
      const res = await apiClient.get(`/api/assigned-teachers/teacher/${teacherId}`);
      if (res.data.status === 'success') {
        const assignments = (res.data.assignments || []).map((a: any) => ({
          id: getId(a),
          class_id: String(a.class_id || ''),
          class_name: a.class_name || '',
          sections: a.sections || [],
          subject_id: String(a.subject_id || ''),
          subject_name: a.subject_name || '',
          created_at: a.created_at || '',
          updated_at: a.updated_at || '',
        }));
        setData(prev => prev ? { 
          ...prev, 
          assignments, 
          summary: res.data.summary || prev.summary 
        } : prev);
      }
    } catch (error) {
      console.error('❌ Failed to refresh assignments:', error);
      // keep whatever we already have (from the list) as a fallback
    } finally {
      setRefreshing(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  /* ─── selection helpers ─── */
  const isAssignmentSelected = (assignmentId: string) => !!selection[assignmentId];  // ✅ Changed to string

  const getSelectedSections = (assignment: TeacherAssignment): string[] => {
    const s = selection[assignment.id];
    if (s === 'ALL') return assignment.sections;
    if (s instanceof Set) return Array.from(s);
    return [];
  };

  const toggleAssignment = (assignment: TeacherAssignment) => {
    setSelection(prev => {
      const next = { ...prev };
      if (next[assignment.id]) delete next[assignment.id];
      else next[assignment.id] = 'ALL';
      return next;
    });
  };

  const handleSectionsChange = (assignment: TeacherAssignment, newSections: string[]) => {
    setSelection(prev => {
      const next = { ...prev };
      if (newSections.length === 0) {
        delete next[assignment.id];
      } else if (newSections.length === assignment.sections.length) {
        next[assignment.id] = 'ALL';
      } else {
        next[assignment.id] = new Set(newSections);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (!data) return;
    const next: SelectionMap = {};
    data.assignments.forEach(a => { next[a.id] = 'ALL'; });
    setSelection(next);
  };

  const clearSelection = () => setSelection({});

  const selectedCount = Object.keys(selection).length;

  const selectedSectionsCount = () => {
    if (!data) return 0;
    let count = 0;
    data.assignments.forEach(a => {
      const s = selection[a.id];
      if (s === 'ALL') count += a.sections.length;
      else if (s instanceof Set) count += s.size;
    });
    return count;
  };

  const handleBulkRemove = async () => {
    if (!data) return;
    setBulkRemoving(true);
    setConfirmOpen(false);
    
    const fullRemoveIds: string[] = [];  // ✅ Changed to string[]
    const sectionRemovals: { assignment_id: string; sections: string[] }[] = [];  // ✅ Changed to string
    
    Object.entries(selection).forEach(([idStr, val]) => {
      const assignment = data.assignments.find(a => a.id === idStr);
      if (!assignment) return;
      if (val === 'ALL') {
        fullRemoveIds.push(idStr);
      } else if (val instanceof Set) {
        const remaining = assignment.sections.filter(s => !(val as Set<string>).has(s));
        if (remaining.length === 0) fullRemoveIds.push(idStr);
        else sectionRemovals.push({ assignment_id: idStr, sections: Array.from(val) });
      }
    });
    
    try {
      const calls: Promise<any>[] = [];
      
      // ✅ Use the correct API endpoints
      if (fullRemoveIds.length > 0) {
        calls.push(apiClient.delete('/api/assigned-teachers/bulk-remove', { 
          data: { assignment_ids: fullRemoveIds } 
        }));
      }
      if (sectionRemovals.length > 0) {
        calls.push(apiClient.delete('/api/assigned-teachers/bulk-remove-sections', { 
          data: { removals: sectionRemovals } 
        }));
      }
      
      await Promise.all(calls);
      toast.current?.show({ severity: 'success', summary: 'Removed', detail: 'Selected assignments removed successfully.', life: 3000 });
      setSelection({});
      await refreshAssignments(data.id);
      onChanged?.();
    } catch (err: any) {
      console.error('❌ Bulk remove error:', err.response?.data || err.message);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to remove assignments.', life: 4000 });
    } finally {
      setBulkRemoving(false);
    }
  };

  /* ─── Header — avatar with gradient progress ring ─── */
  const headerTemplate = () => {
    const c = data ? avatarColor(data.id) : { bg: '#dbeafe', color: '#1e40af' };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: c.bg, color: c.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '30px', fontWeight: 700,
            border: '3px solid white', boxShadow: '0 8px 24px rgba(65, 105, 225, 0.25)',
            flexShrink: 0,
          }}>
            {data ? getInitials(data.name) : '?'}
          </div>
          <svg style={{
            position: 'absolute', top: '-4px', left: '-4px',
            width: '88px', height: '88px', transform: 'rotate(-90deg)', pointerEvents: 'none'
          }}>
            <circle
              cx="44" cy="44" r="41" fill="none"
              stroke="url(#teacherGradientRing)" strokeWidth="2.5"
              strokeDasharray="258" strokeDashoffset="64.5" strokeLinecap="round"
              style={{ filter: 'drop-shadow(0 0 4px rgba(65, 105, 225, 0.4))' }}
            />
            <defs>
              <linearGradient id="teacherGradientRing" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4169E1" />
                <stop offset="50%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            {data?.name || 'Teacher Profile'}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {data?.email_id || 'Complete assignment overview'}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Small reusable info tile for the summary strip ─── */
  const SummaryTile = ({ icon, label, value, color, bg }: { icon: string; label: string; value: number; color: string; bg: string }) => (
    <div style={{
      flex: '1 1 130px', background: bg, borderRadius: 10, padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: 10
    }}>
      <i className={`pi ${icon}`} style={{ color, fontSize: 18 }} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: 11, color, opacity: 0.8, fontWeight: 600 }}>{label}</div>
      </div>
    </div>
  );

  /* ─── Section header ─── */
  const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      marginBottom: '14px', paddingBottom: '12px',
      borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '32px', height: '32px', borderRadius: '8px',
        background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white'
      }}>
        <i className={icon} style={{ fontSize: '15px' }}></i>
      </div>
      <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
        {title}
      </span>
    </div>
  );

  const assignments = data?.assignments ?? [];

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '760px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none'
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px'
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important; }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }

          .vtm-toolbar-btn {
            border: none; background: none; cursor: pointer;
            font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; gap: 6px;
            padding: 6px 10px; border-radius: 8px; transition: background 0.15s;
          }
          .vtm-toolbar-btn:hover { background: rgba(0,0,0,0.04); }
          .vtm-assignment-row {
            border-radius: 12px; background: #fff; transition: all 0.2s; overflow: hidden;
          }
        `}</style>

        {!data ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 20px', gap: '16px'
          }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
              Loading teacher details...
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Assignment Summary */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '16px 18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <SectionHeader icon="pi pi-chart-bar" title="Assignment Summary" />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <SummaryTile icon="pi-book" label="Subjects" value={data.summary?.total_subjects ?? 0} bg="#dbeafe" color="#1e40af" />
                <SummaryTile icon="pi-th-large" label="Classes" value={data.summary?.total_classes ?? 0} bg="#dcfce7" color="#166534" />
                <SummaryTile icon="pi-list" label="Assignments" value={data.summary?.total_assignments ?? 0} bg="#fef3c7" color="#92400e" />
                <SummaryTile icon="pi-users" label="Sections" value={data.summary?.total_sections ?? 0} bg="#ede9fe" color="#4c1d95" />
              </div>
            </div>

            {/* Contact */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '16px 18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <SectionHeader icon="pi pi-phone" title="Contact Information" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(65,105,225,0.08)', color: '#4169E1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="pi pi-envelope" style={{ fontSize: 15 }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{data.email_id || '—'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(65,105,225,0.08)', color: '#4169E1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="pi pi-phone" style={{ fontSize: 15 }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{data.contact_no || '—'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(65,105,225,0.08)', color: '#4169E1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="pi pi-users" style={{ fontSize: 15 }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{data.gender || '—'}</div>
                </div>
              </div>
            </div>

            {/* Assigned Subjects */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <SectionHeader icon="pi pi-book" title="Assigned Subjects" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: -14 }}>
                  {refreshing && <ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="5" />}
                  {selectedCount > 0 ? (
                    <>
                      <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
                        {selectedSectionsCount()} section{selectedSectionsCount() !== 1 ? 's' : ''} selected
                      </span>
                      <button className="vtm-toolbar-btn" style={{ color: '#6b7280' }} onClick={clearSelection}>
                        <i className="pi pi-times" /> Clear
                      </button>
                      <button
                        className="vtm-toolbar-btn"
                        style={{ color: '#ef4444', background: '#fee2e2' }}
                        disabled={bulkRemoving}
                        onClick={() => setConfirmOpen(true)}
                      >
                        <i className={bulkRemoving ? 'pi pi-spin pi-spinner' : 'pi pi-trash'} />
                        Remove ({selectedSectionsCount()})
                      </button>
                    </>
                  ) : assignments.length > 0 && (
                    <button className="vtm-toolbar-btn" style={{ color: '#4169E1' }} onClick={selectAll}>
                      <i className="pi pi-check-square" /> Select all
                    </button>
                  )}
                </div>
              </div>

              {assignments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#9ca3af', fontSize: 14 }}>
                  No subject assignments found for this teacher.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {assignments.map(a => {
                    const isRowSelected = isAssignmentSelected(a.id);
                    return (
                      <div
                        key={a.id}
                        className="vtm-assignment-row"
                        style={{
                          border: isRowSelected ? '1.5px solid #ef4444' : '1px solid #e5e7eb',
                          background: isRowSelected ? '#fff5f5' : '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
                          <div style={{ width: 22, flexShrink: 0 }}>
                            <GlobalCheckboxes
                              options={[{ value: a.id, label: '' }]}
                              value={isRowSelected ? [a.id] : []}
                              onChange={() => toggleAssignment(a)}
                              gap={0}
                            />
                          </div>
                          <div style={{
                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                            background: 'linear-gradient(135deg, #4169E1, #5B7CE6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <i className="pi pi-book" style={{ color: '#fff', fontSize: 14 }} />
                          </div>
                          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {a.subject_name}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                              {a.class_name} &nbsp;·&nbsp; Assignment #{a.id.substring(0, 8)}
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 10px', borderRadius: 20,
                            background: '#eff6ff', color: '#1e40af', fontSize: 11, fontWeight: 700,
                            border: '1px solid #bfdbfe', flexShrink: 0
                          }}>
                            {a.sections.length} section{a.sections.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {a.sections.length > 0 && (
                          <div style={{
                            padding: '0 14px 12px',
                            borderTop: '1px dashed #f3f4f6', paddingTop: 10
                          }}>
                            <GlobalCheckboxes
                              options={a.sections.map(sec => ({ value: sec, label: sec }))}
                              value={getSelectedSections(a)}
                              onChange={(vals) => handleSectionsChange(a, vals)}
                              layout="horizontal"
                              gap={16}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      {/* ── Confirm removal ── */}
      <Dialog
        header="Confirm removal"
        visible={confirmOpen}
        onHide={() => setConfirmOpen(false)}
        style={{ width: 400 }}
        modal
        draggable={false}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setConfirmOpen(false)}
              style={{
                padding: '9px 16px', borderRadius: 8, border: 'none',
                background: 'transparent', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleBulkRemove}
              disabled={bulkRemoving}
              style={{
                padding: '9px 16px', borderRadius: 8, border: 'none',
                background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13,
                cursor: bulkRemoving ? 'not-allowed' : 'pointer',
                opacity: bulkRemoving ? 0.7 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className={bulkRemoving ? 'pi pi-spin pi-spinner' : 'pi pi-trash'} />
              {bulkRemoving ? 'Removing…' : 'Yes, remove'}
            </button>
          </div>
        }
      >
        <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          You are about to remove <strong>{selectedSectionsCount()} section{selectedSectionsCount() !== 1 ? 's' : ''}</strong> across{' '}
          <strong>{selectedCount} assignment{selectedCount !== 1 ? 's' : ''}</strong> from{' '}
          <strong>{data?.name}</strong>. This cannot be undone.
        </div>
      </Dialog>
    </>
  );
};

export default ViewTeacherModal;